﻿import { useEffect, useMemo, useState } from "react";
import { useSession } from "@/auth/session";
import { changeMyPassword, updateMyProfile } from "@/lib/api";

interface MiPerfilModalProps {
  onClose: () => void;
}

type Tab = "perfil" | "seguridad";

export default function MiPerfilModal({ onClose }: MiPerfilModalProps) {
  const { session, profile, refreshProfile } = useSession();
  const [tab, setTab] = useState<Tab>("perfil");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [perfilError, setPerfilError] = useState("");

  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");

  const [passwords, setPasswords] = useState({
    actual: "",
    nueva: "",
    confirmar: "",
  });
  const [pwError, setPwError] = useState("");
  const [pwSaved, setPwSaved] = useState(false);

  useEffect(() => {
    setFullName(profile?.fullName ?? "");
    setEmail(profile?.email ?? "");
    setPhone(profile?.phone ?? "");
  }, [profile?.fullName, profile?.email, profile?.phone]);

  const roleLabel = useMemo(() => {
    const roleCode = profile?.roles?.[0];
    const labels: Record<string, string> = {
      admin: "Administrador",
      operator: "Operador",
      cashier: "Cajero",
      field_reader: "Lector de Terreno",
      readonly: "Solo Lectura",
      billing: "Facturación",
    };
    if (!roleCode) return "Sin rol";
    return labels[roleCode] ?? roleCode;
  }, [profile?.roles]);

  const initials = useMemo(() => {
    const base = (fullName || profile?.fullName || "Usuario APR")
      .split(" ")
      .map((n) => n.trim())
      .filter(Boolean)
      .slice(0, 2)
      .map((n) => n[0]?.toUpperCase() ?? "")
      .join("");
    return base || "UA";
  }, [fullName, profile?.fullName]);

  async function handleSavePerfil(): Promise<void> {
    if (!session) return;
    setPerfilError("");
    setSaved(false);
    const normalizedName = fullName.trim();
    if (!normalizedName) {
      setPerfilError("El nombre completo es obligatorio.");
      return;
    }

    setSaving(true);
    try {
      await updateMyProfile(session, {
        fullName: normalizedName,
        phone: phone.trim() || null,
      });
      await refreshProfile();
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (error) {
      setPerfilError(error instanceof Error ? error.message : "No se pudo actualizar el perfil.");
    } finally {
      setSaving(false);
    }
  }

  async function handleSavePassword(): Promise<void> {
    if (!session) return;
    setPwError("");
    if (!passwords.actual) {
      setPwError("Ingresa tu contraseña actual.");
      return;
    }
    if (passwords.nueva.length < 8) {
      setPwError("La nueva contraseña debe tener al menos 8 caracteres.");
      return;
    }
    if (passwords.nueva !== passwords.confirmar) {
      setPwError("Las contraseñas no coinciden.");
      return;
    }

    setSaving(true);
    try {
      await changeMyPassword(session, {
        currentPassword: passwords.actual,
        newPassword: passwords.nueva,
      });
      setPwSaved(true);
      setPasswords({ actual: "", nueva: "", confirmar: "" });
      setTimeout(() => setPwSaved(false), 2500);
    } catch (error) {
      setPwError(error instanceof Error ? error.message : "No se pudo cambiar la contraseña.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/30 overflow-y-auto p-4 pt-24 pb-8">
      <div className="bg-white rounded-2xl border border-gray-100 w-full max-w-lg overflow-hidden max-h-[calc(100vh-5rem)] overflow-y-auto shadow-lg">
        <div className="relative bg-gradient-to-r from-teal-600 to-teal-500 px-6 pt-6 pb-14">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-lg bg-white/20 hover:bg-white/30 text-white cursor-pointer transition-colors"
          >
            <i className="ri-close-line text-lg" />
          </button>
          <h2 className="text-white font-semibold text-base">Mi Perfil</h2>
          <p className="text-teal-100 text-xs mt-0.5">Gestiona tu información personal y seguridad</p>
        </div>

        <div className="flex justify-center mt-1 mb-4">
          <div className="w-20 h-20 flex items-center justify-center rounded-full bg-teal-600 text-white text-2xl font-bold border-4 border-white shadow-sm">
            {initials}
          </div>
        </div>

        <div className="text-center mb-4">
          <div className="text-base font-semibold text-gray-800">{fullName || "Usuario APR"}</div>
          <span className="inline-block mt-1 px-3 py-0.5 bg-teal-50 text-teal-700 text-xs font-medium rounded-full border border-teal-100">
            {roleLabel}
          </span>
        </div>

        <div className="flex gap-1 mx-6 mb-5 bg-gray-100 rounded-lg p-1">
          {(["perfil", "seguridad"] as Tab[]).map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`flex-1 py-1.5 text-sm font-medium rounded-md transition-colors cursor-pointer whitespace-nowrap ${
                tab === t ? "bg-white text-gray-800 shadow-sm" : "text-gray-500 hover:text-gray-700"
              }`}
            >
              {t === "perfil" ? "Información" : "Seguridad"}
            </button>
          ))}
        </div>

        <div className="px-6 pb-6">
          {tab === "perfil" && (
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1.5">Nombre completo</label>
                <input
                  type="text"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-teal-400 transition-colors"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1.5">Correo electrónico</label>
                <input
                  type="email"
                  value={email}
                  readOnly
                  className="w-full px-3 py-2 text-sm border border-gray-100 rounded-lg bg-gray-50 text-gray-500 select-none"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1.5">Teléfono</label>
                <input
                  type="text"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-teal-400 transition-colors"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1.5">Rol en el sistema</label>
                <div className="w-full px-3 py-2 text-sm border border-gray-100 rounded-lg bg-gray-50 text-gray-500 select-none">
                  {roleLabel} <span className="text-xs text-gray-400">(solo lectura)</span>
                </div>
              </div>

              {perfilError ? (
                <div className="flex items-center gap-2 px-3 py-2 bg-red-50 border border-red-100 rounded-lg text-red-600 text-sm">
                  <i className="ri-error-warning-line" />
                  {perfilError}
                </div>
              ) : null}
              {saved ? (
                <div className="flex items-center gap-2 px-3 py-2 bg-emerald-50 border border-emerald-100 rounded-lg text-emerald-700 text-sm">
                  <i className="ri-checkbox-circle-line" />
                  Perfil actualizado correctamente
                </div>
              ) : null}

              <div className="flex gap-3 pt-1">
                <button
                  onClick={onClose}
                  className="flex-1 px-4 py-2 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer whitespace-nowrap transition-colors"
                >
                  Cerrar
                </button>
                <button
                  onClick={() => void handleSavePerfil()}
                  disabled={saving || !session}
                  className="flex-1 px-4 py-2 text-sm bg-teal-600 text-white rounded-lg hover:bg-teal-700 cursor-pointer whitespace-nowrap transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
                >
                  {saving ? (
                    <>
                      <div className="w-3.5 h-3.5 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                      Guardando...
                    </>
                  ) : "Guardar cambios"}
                </button>
              </div>
            </div>
          )}

          {tab === "seguridad" && (
            <div className="space-y-4">
              <div className="flex items-start gap-3 px-3 py-3 bg-amber-50 border border-amber-100 rounded-lg">
                <div className="w-4 h-4 flex items-center justify-center text-amber-600 mt-0.5 flex-shrink-0">
                  <i className="ri-shield-keyhole-line" />
                </div>
                <p className="text-xs text-amber-700 leading-relaxed">
                  Usa una contraseña de al menos 8 caracteres con letras y números para mayor seguridad.
                </p>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1.5">Contraseña actual</label>
                <input
                  type="password"
                  value={passwords.actual}
                  onChange={(e) => setPasswords((p) => ({ ...p, actual: e.target.value }))}
                  placeholder="••••••••"
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-teal-400 transition-colors"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1.5">Nueva contraseña</label>
                <input
                  type="password"
                  value={passwords.nueva}
                  onChange={(e) => setPasswords((p) => ({ ...p, nueva: e.target.value }))}
                  placeholder="Mínimo 8 caracteres"
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-teal-400 transition-colors"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1.5">Confirmar nueva contraseña</label>
                <input
                  type="password"
                  value={passwords.confirmar}
                  onChange={(e) => setPasswords((p) => ({ ...p, confirmar: e.target.value }))}
                  placeholder="Repite la contraseña"
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-teal-400 transition-colors"
                />
              </div>

              {pwError ? (
                <div className="flex items-center gap-2 px-3 py-2 bg-red-50 border border-red-100 rounded-lg text-red-600 text-sm">
                  <i className="ri-error-warning-line" />
                  {pwError}
                </div>
              ) : null}
              {pwSaved ? (
                <div className="flex items-center gap-2 px-3 py-2 bg-emerald-50 border border-emerald-100 rounded-lg text-emerald-700 text-sm">
                  <i className="ri-checkbox-circle-line" />
                  Contraseña actualizada correctamente
                </div>
              ) : null}

              <div className="flex gap-3 pt-1">
                <button
                  onClick={onClose}
                  className="flex-1 px-4 py-2 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer whitespace-nowrap transition-colors"
                >
                  Cerrar
                </button>
                <button
                  onClick={() => void handleSavePassword()}
                  disabled={saving || !session}
                  className="flex-1 px-4 py-2 text-sm bg-teal-600 text-white rounded-lg hover:bg-teal-700 cursor-pointer whitespace-nowrap transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
                >
                  {saving ? (
                    <>
                      <div className="w-3.5 h-3.5 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                      Guardando...
                    </>
                  ) : "Cambiar contraseña"}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
